import React, { useState, useRef, useCallback, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Upload,
  FileSpreadsheet,
  Check,
  X,
  AlertCircle,
  ArrowRight,
  Trash2,
  Eye,
  Loader2,
  Sparkles,
  Wand2,
  Edit3,
  ChevronLeft,
  ChevronRight,
  Link2,
  Plus,
} from "lucide-react";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import { DataService } from "../services/supabase";
import { Transaction } from "../types";

// Inventory fields that can be mapped to
const INVENTORY_FIELDS: { key: keyof Transaction; label: string; required: boolean }[] = [
  { key: "card", label: "Card Name", required: true },
  { key: "purchase_date", label: "Date Purchased", required: true },
  { key: "cost", label: "Purchase Price", required: true },
  { key: "manufacturer", label: "Manufacturer", required: false },
  { key: "set", label: "Set", required: false },
  { key: "card_number", label: "Card #", required: false },
  { key: "subject", label: "Player / Character", required: false },
  { key: "league", label: "League", required: false },
  { key: "insert_name", label: "Insert / Subset", required: false },
  { key: "parallel", label: "Parallel", required: false },
  { key: "numbering", label: "Print Run / Numbering", required: false },
  { key: "year", label: "Year", required: false },
  { key: "sport", label: "Sport", required: false },
  { key: "purchase_platform", label: "Purchase Platform", required: false },
  { key: "grade", label: "Grade", required: false },
  { key: "grading_order_id", label: "Grading Order ID", required: false },
  { key: "bought_from", label: "Bought From", required: false },
  { key: "notes", label: "Notes", required: false },
  { key: "other", label: "Other", required: false },
  // Sale-related fields
  { key: "sale_date", label: "Sale Date", required: false },
  { key: "sale_price", label: "Sale Price", required: false },
  { key: "sale_platform", label: "Sale Platform", required: false },
  { key: "sold_to", label: "Sold To", required: false },
  { key: "marketplace_fees", label: "Marketplace Fees", required: false },
  { key: "shipping_fees", label: "Shipping Fees", required: false },
];

// Common column name variations for auto-mapping
const COLUMN_MAPPINGS: Record<string, keyof Transaction> = {
  // Card name variations
  "card": "card",
  "card name": "card",
  "cardname": "card",
  "name": "card",
  "player": "card",
  "player name": "card",
  "playername": "card",
  "item": "card",
  "item name": "card",
  "itemname": "card",
  "title": "card",
  "description": "card",
  
  // Date variations
  "purchase date": "purchase_date",
  "purchasedate": "purchase_date",
  "date purchased": "purchase_date",
  "datepurchased": "purchase_date",
  "date": "purchase_date",
  "purchase_date": "purchase_date",
  "bought date": "purchase_date",
  "buy date": "purchase_date",
  "buydate": "purchase_date",
  "acquisition date": "purchase_date",
  "acquired": "purchase_date",
  
  // Price variations
  "cost": "cost",
  "price": "cost",
  "purchase price": "cost",
  "purchaseprice": "cost",
  "buy price": "cost",
  "buyprice": "cost",
  "amount": "cost",
  "purchase_price": "cost",
  "purchase amount": "cost",
  "paid": "cost",
  "total": "cost",
  
  // Manufacturer variations
  "manufacturer": "manufacturer",
  "brand": "manufacturer",
  "company": "manufacturer",
  "maker": "manufacturer",
  
  // Set variations
  "set": "set",
  "setname": "set",
  "set name": "set",
  "collection": "set",
  "series": "set",
  "product": "set",

  "card #": "card_number",
  "card number": "card_number",
  "card_number": "card_number",
  "cardno": "card_number",
  "catalog #": "card_number",

  "subject": "subject",
  "player / character": "subject",
  "player character": "subject",
  "character": "subject",
  "athlete": "subject",

  "league": "league",
  "circuit": "league",

  "insert": "insert_name",
  "subset": "insert_name",
  "insert name": "insert_name",
  "insert_name": "insert_name",
  "insert set": "insert_name",

  "parallel": "parallel",
  "color": "parallel",
  "variant": "parallel",

  "numbering": "numbering",
  "serial": "numbering",
  "print run": "numbering",
  "numbered": "numbering",
  
  // Year variations
  "year": "year",
  "card year": "year",
  "release year": "year",
  "release_year": "year",
  
  // Sport variations
  "sport": "sport",
  "category": "sport",
  "type": "sport",
  
  // Platform variations
  "purchase platform": "purchase_platform",
  "purchaseplatform": "purchase_platform",
  "platform": "purchase_platform",
  "source": "purchase_platform",
  "where bought": "purchase_platform",
  "marketplace": "purchase_platform",
  "venue": "purchase_platform",
  "purchase_platform": "purchase_platform",
  
  // Grade variations
  "grade": "grade",
  "grading": "grade",
  "condition": "grade",
  "psa": "grade",
  "bgs": "grade",
  "sgc": "grade",
  
  // Grading order variations
  "grading order id": "grading_order_id",
  "grading_order_id": "grading_order_id",
  "order id": "grading_order_id",
  "submission id": "grading_order_id",
  "cert": "grading_order_id",
  "cert number": "grading_order_id",
  
  // Seller variations
  "bought from": "bought_from",
  "boughtfrom": "bought_from",
  "seller": "bought_from",
  "vendor": "bought_from",
  "from": "bought_from",
  "bought_from": "bought_from",
  
  // Notes variations
  "notes": "notes",
  "note": "notes",
  "comments": "notes",
  "comment": "notes",
  "memo": "notes",
  "remarks": "notes",
  
  // Other variations
  "other": "other",
  "misc": "other",
  "additional": "other",
  "extra": "other",
  
  // Sale date variations
  "sale date": "sale_date",
  "saledate": "sale_date",
  "date sold": "sale_date",
  "datesold": "sale_date",
  "sold date": "sale_date",
  "solddate": "sale_date",
  "sale_date": "sale_date",
  "sell date": "sale_date",
  "sold": "sale_date",
  
  // Sale price variations
  "sale price": "sale_price",
  "saleprice": "sale_price",
  "sale_price": "sale_price",
  "sold price": "sale_price",
  "soldprice": "sale_price",
  "sell price": "sale_price",
  "sellprice": "sale_price",
  "sold for": "sale_price",
  "selling price": "sale_price",
  "revenue": "sale_price",
  "sale amount": "sale_price",
  "saleamount": "sale_price",
  
  // Sale platform variations
  "sale platform": "sale_platform",
  "saleplatform": "sale_platform",
  "sale_platform": "sale_platform",
  "sold on": "sale_platform",
  "sold platform": "sale_platform",
  "selling platform": "sale_platform",
  "sold via": "sale_platform",
  "where sold": "sale_platform",
  
  // Sold to variations
  "sold to": "sold_to",
  "soldto": "sold_to",
  "sold_to": "sold_to",
  "buyer": "sold_to",
  "customer": "sold_to",
  "purchaser": "sold_to",
  
  // Marketplace fees variations
  "marketplace fees": "marketplace_fees",
  "marketplacefees": "marketplace_fees",
  "marketplace_fees": "marketplace_fees",
  "platform fees": "marketplace_fees",
  "fees": "marketplace_fees",
  "selling fees": "marketplace_fees",
  "commission": "marketplace_fees",
  "ebay fees": "marketplace_fees",
  "marketplace fee": "marketplace_fees",
  "marketplacefee": "marketplace_fees",
  
  // Shipping fees variations
  "shipping fees": "shipping_fees",
  "shippingfees": "shipping_fees",
  "shipping_fees": "shipping_fees",
  "shipping": "shipping_fees",
  "shipping cost": "shipping_fees",
  "shippingcost": "shipping_fees",
  "postage": "shipping_fees",
  
  // Additional bought from variations  
  "purchased from": "bought_from",
  "purchasedfrom": "bought_from",
};

// Known manufacturers for smart extraction
const KNOWN_MANUFACTURERS = [
  "Topps", "Panini", "Upper Deck", "Bowman", "Donruss", "Fleer", 
  "Score", "Leaf", "Sage", "Press Pass", "Contenders", "National Treasures",
  "Optic", "Mosaic", "Select", "Prizm", "Chronicles", "Absolute",
  "Spectra", "Immaculate", "Noir", "One", "Origins", "Phoenix",
  "Obsidian", "Certified", "Limited", "Illusions", "Hoops", "Prestige",
  "Court Kings", "Revolution", "Crown Royale", "Cornerstones", "Status",
  "Encased", "Eminence", "Gold Standard", "Majestic", "National Pride",
  "Titanium", "Elite", "Rookies & Stars", "Playoff", "SP", "SPx",
  "UD", "Exquisite", "The Cup", "Artifacts", "O-Pee-Chee", "OPC",
  "Skybox", "Metal", "Flair", "Finest", "Tek", "Inception",
  "Stadium Club", "Heritage", "Archives", "Allen & Ginter", "A&G",
  "Gypsy Queen", "Diamond Kings", "Luminance", "Clearly Donruss",
];

// Known set names for smart extraction
const KNOWN_SETS = [
  "Chrome", "Prizm", "Select", "Mosaic", "Optic", "Donruss", "Hoops",
  "Contenders", "National Treasures", "Immaculate", "Spectra", "Noir",
  "One", "Origins", "Phoenix", "Obsidian", "Certified", "Limited",
  "Illusions", "Prestige", "Court Kings", "Revolution", "Crown Royale",
  "Cornerstones", "Status", "Encased", "Eminence", "Gold Standard",
  "Heritage", "Archives", "Allen & Ginter", "Gypsy Queen", "Diamond Kings",
  "Stadium Club", "Finest", "Bowman", "Bowman Chrome", "Bowman Draft",
  "Bowman Sterling", "Bowman Platinum", "Topps Update", "Topps Series 1",
  "Topps Series 2", "Topps Opening Day", "Topps Fire", "Topps Gold Label",
  "Flawless", "Noir", "Inception", "Tek", "Triple Threads", "Tribute",
  "Museum Collection", "Dynasty", "Transcendent", "Luminaries", "Definitive",
  "Five Star", "Pro Bowl", "Super Bowl", "All-Pro", "Rookies & Stars",
  "Classics", "Absolute", "Threads", "Elite", "Score", "Playoff",
  "Clear Vision", "Ascension", "Recon", "Zenith", "XR", "Spectra",
];

// Known sports for smart extraction
const KNOWN_SPORTS = [
  "Baseball", "Basketball", "Football", "Hockey", "Soccer", "Golf",
  "Tennis", "Boxing", "MMA", "UFC", "Wrestling", "Racing", "NASCAR",
  "F1", "Cricket", "Rugby", "Lacrosse", "Volleyball", "Softball",
];

// Fields that should be excluded from year extraction (contain dates, not card years)
const DATE_FIELD_PATTERNS = [
  /date/i, /purchased/i, /sold/i, /sale/i, /acquired/i, /bought/i
];

// Grade patterns for smart extraction
const GRADE_PATTERNS = [
  // PSA grades
  { pattern: /\bPSA\s*(\d{1,2}(?:\.\d)?)\b/i, format: (m: RegExpMatchArray) => `PSA ${m[1]}` },
  { pattern: /\bGEM\s*(?:MT|MINT)?\s*(\d{1,2})\b/i, format: (m: RegExpMatchArray) => `PSA ${m[1]}` },
  // BGS grades
  { pattern: /\bBGS\s*(\d{1,2}(?:\.\d)?)\b/i, format: (m: RegExpMatchArray) => `BGS ${m[1]}` },
  { pattern: /\bBECKETT\s*(\d{1,2}(?:\.\d)?)\b/i, format: (m: RegExpMatchArray) => `BGS ${m[1]}` },
  // SGC grades
  { pattern: /\bSGC\s*(\d{1,2}(?:\.\d)?)\b/i, format: (m: RegExpMatchArray) => `SGC ${m[1]}` },
  // CSG grades
  { pattern: /\bCSG\s*(\d{1,2}(?:\.\d)?)\b/i, format: (m: RegExpMatchArray) => `CSG ${m[1]}` },
  // CGC grades
  { pattern: /\bCGC\s*(\d{1,2}(?:\.\d)?)\b/i, format: (m: RegExpMatchArray) => `CGC ${m[1]}` },
  // HGA grades
  { pattern: /\bHGA\s*(\d{1,2}(?:\.\d)?)\b/i, format: (m: RegExpMatchArray) => `HGA ${m[1]}` },
  // Generic numeric grade
  { pattern: /\b(?:GRADE|GR|GRADED)\s*(\d{1,2}(?:\.\d)?)\b/i, format: (m: RegExpMatchArray) => m[1] },
];

interface ColumnMapping {
  sourceColumn: string;
  targetField: keyof Transaction | "skip";
  sampleValues: string[];
}

// Columns to be combined into a single field
interface ColumnCombination {
  targetField: keyof Transaction;
  sourceColumns: string[];
  separator: string; // How to join the values, e.g., " " for "PSA 10"
}

interface ParsedData {
  headers: string[];
  rows: Record<string, string>[];
  mappings: ColumnMapping[];
  combinations: ColumnCombination[];
}

// Transformed row with all inventory fields
interface TransformedRow {
  id: number;
  data: Partial<Transaction>;
  originalRow: Record<string, string>;
}

interface InventoryImportProps {
  dataService: DataService | null;
  onImportComplete: () => void;
}

const InventoryImport: React.FC<InventoryImportProps> = ({ dataService, onImportComplete }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [parsedData, setParsedData] = useState<ParsedData | null>(null);
  const [showMappingDialog, setShowMappingDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState(false);
  const [fileName, setFileName] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Editable transformed data
  const [transformedRows, setTransformedRows] = useState<TransformedRow[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [editingCell, setEditingCell] = useState<{ rowId: number; field: keyof Transaction } | null>(null);
  const [extractionApplied, setExtractionApplied] = useState(false);
  
  // Column combination state
  const [showCombineDialog, setShowCombineDialog] = useState(false);
  const [combineTargetField, setCombineTargetField] = useState<keyof Transaction>("grade");
  const [combineSelectedColumns, setCombineSelectedColumns] = useState<string[]>([]);
  const [combineSeparator, setCombineSeparator] = useState(" ");
  
  const ROWS_PER_PAGE = 10;

  // Smart extraction functions
  const extractYear = useCallback((text: string): number | null => {
    // Look for 4-digit years between 1900 and current year + 1
    const currentYear = new Date().getFullYear();
    const yearRegex = /\b(19[0-9]{2}|20[0-2][0-9]|203[0-5])\b/g;
    const matches = text.match(yearRegex);
    
    if (matches) {
      // Prefer years that make sense for trading cards (1948-present)
      const validYears = matches
        .map(y => parseInt(y, 10))
        .filter(y => y >= 1948 && y <= currentYear + 1);
      
      if (validYears.length > 0) {
        // Return the most recent year found (likely the release year)
        return Math.max(...validYears);
      }
    }
    return null;
  }, []);

  const extractManufacturer = useCallback((text: string): string | null => {
    for (const manufacturer of KNOWN_MANUFACTURERS) {
      const regex = new RegExp(`\\b${manufacturer.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
      if (regex.test(text)) {
        return manufacturer;
      }
    }
    return null;
  }, []);

  const extractSet = useCallback((text: string): string | null => {
    for (const setName of KNOWN_SETS) {
      const regex = new RegExp(`\\b${setName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
      if (regex.test(text)) {
        return setName;
      }
    }
    return null;
  }, []);

  const extractSport = useCallback((text: string): string | null => {
    const upperText = text.toUpperCase();
    
    for (const sport of KNOWN_SPORTS) {
      if (upperText.includes(sport.toUpperCase())) {
        return sport;
      }
    }
    
    // Check for sport-specific keywords
    if (/\b(MLB|YANKEES|RED SOX|DODGERS|METS|CUBS|CARDINALS|GIANTS|PHILLIES|BRAVES|ASTROS)\b/i.test(text)) {
      return "Baseball";
    }
    if (/\b(NBA|LAKERS|CELTICS|BULLS|WARRIORS|HEAT|NETS|KNICKS|76ERS|BUCKS|SUNS)\b/i.test(text)) {
      return "Basketball";
    }
    if (/\b(NFL|PATRIOTS|COWBOYS|PACKERS|CHIEFS|49ERS|EAGLES|RAVENS|BILLS|DOLPHINS|BRONCOS)\b/i.test(text)) {
      return "Football";
    }
    if (/\b(NHL|BRUINS|RANGERS|PENGUINS|BLACKHAWKS|MAPLE LEAFS|CANADIENS|OILERS|AVALANCHE)\b/i.test(text)) {
      return "Hockey";
    }
    if (/\b(FIFA|PREMIER LEAGUE|LA LIGA|BUNDESLIGA|SERIE A|CHAMPIONS LEAGUE)\b/i.test(text)) {
      return "Soccer";
    }
    
    return null;
  }, []);

  const extractGrade = useCallback((text: string): string | null => {
    for (const gradePattern of GRADE_PATTERNS) {
      const match = text.match(gradePattern.pattern);
      if (match) {
        return gradePattern.format(match);
      }
    }
    return null;
  }, []);

  // Auto-map columns based on header names (preventing duplicates)
  const autoMapColumns = useCallback((headers: string[], rows: Record<string, string>[]): ColumnMapping[] => {
    const usedFields = new Set<keyof Transaction>();
    
    return headers.map((header) => {
      const normalizedHeader = header.toLowerCase().trim();
      const mappedField = COLUMN_MAPPINGS[normalizedHeader];
      let targetField: keyof Transaction | "skip" = mappedField || "skip";
      
      // If this field is already mapped, skip this column to prevent duplicates
      if (mappedField && usedFields.has(mappedField)) {
        targetField = "skip";
      } else if (mappedField) {
        usedFields.add(mappedField);
      }
      
      // Get sample values (first 3 non-empty values)
      const sampleValues = rows
        .slice(0, 5)
        .map((row) => row[header])
        .filter((val) => val && val.trim() !== "")
        .slice(0, 3);

      return {
        sourceColumn: header,
        targetField,
        sampleValues,
      };
    });
  }, []);

  // Parse CSV file
  const parseCSV = useCallback((content: string): Promise<{ headers: string[]; rows: Record<string, string>[] }> => {
    return new Promise((resolve, reject) => {
      Papa.parse(content, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.errors.length > 0) {
            console.warn("CSV parsing warnings:", results.errors);
          }
          const headers = results.meta.fields || [];
          resolve({
            headers,
            rows: results.data as Record<string, string>[],
          });
        },
        error: (error) => {
          reject(error);
        },
      });
    });
  }, []);

  // Parse Excel file
  const parseExcel = useCallback((buffer: ArrayBuffer): { headers: string[]; rows: Record<string, string>[] } => {
    const workbook = XLSX.read(buffer, { type: "array" });
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    
    // Convert to JSON with header row
    const jsonData = XLSX.utils.sheet_to_json<Record<string, any>>(worksheet, {
      raw: false,
      defval: "",
    });
    
    if (jsonData.length === 0) {
      return { headers: [], rows: [] };
    }

    // Get headers from first row keys
    const headers = Object.keys(jsonData[0]);
    
    // Convert all values to strings
    const rows = jsonData.map((row) => {
      const stringRow: Record<string, string> = {};
      for (const key of headers) {
        stringRow[key] = String(row[key] ?? "");
      }
      return stringRow;
    });

    return { headers, rows };
  }, []);

  // Handle file selection
  const handleFileSelect = useCallback(async (file: File) => {
    setImportError(null);
    setImportSuccess(false);
    setFileName(file.name);
    setExtractionApplied(false);

    try {
      const isCSV = file.name.toLowerCase().endsWith(".csv");
      const isExcel = file.name.toLowerCase().endsWith(".xlsx") || file.name.toLowerCase().endsWith(".xls");

      if (!isCSV && !isExcel) {
        throw new Error("Please upload a CSV or Excel (.xlsx, .xls) file");
      }

      let parsed: { headers: string[]; rows: Record<string, string>[] };

      if (isCSV) {
        const content = await file.text();
        parsed = await parseCSV(content);
      } else {
        const buffer = await file.arrayBuffer();
        parsed = parseExcel(buffer);
      }

      if (parsed.headers.length === 0) {
        throw new Error("File appears to be empty or has no headers");
      }

      if (parsed.rows.length === 0) {
        throw new Error("File has no data rows");
      }

      const mappings = autoMapColumns(parsed.headers, parsed.rows);
      
      setParsedData({
        headers: parsed.headers,
        rows: parsed.rows,
        mappings,
        combinations: [], // No combinations by default
      });
      
      setShowMappingDialog(true);
    } catch (error: any) {
      setImportError(error.message || "Failed to parse file");
      setParsedData(null);
    }
  }, [parseCSV, parseExcel, autoMapColumns]);

  // Handle drag events
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  // Handle click to upload
  const handleClickUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
    // Reset input so the same file can be selected again
    e.target.value = "";
  }, [handleFileSelect]);

  // Update mapping for a column
  const updateMapping = useCallback((sourceColumn: string, targetField: keyof Transaction | "skip") => {
    if (!parsedData) return;

    setParsedData((prev) => {
      if (!prev) return null;
      
      // If this column is part of a combination for this field, remove it from combinations
      let updatedCombinations = prev.combinations;
      if (targetField === "skip") {
        updatedCombinations = prev.combinations.map((combo) => ({
          ...combo,
          sourceColumns: combo.sourceColumns.filter((col) => col !== sourceColumn),
        })).filter((combo) => combo.sourceColumns.length > 1);
      }
      
      return {
        ...prev,
        mappings: prev.mappings.map((m) =>
          m.sourceColumn === sourceColumn ? { ...m, targetField } : m
        ),
        combinations: updatedCombinations,
      };
    });
  }, [parsedData]);

  // Create a combination of multiple columns into one field
  const createColumnCombination = useCallback((targetField: keyof Transaction, sourceColumns: string[], separator: string = " ") => {
    if (!parsedData) return;
    
    setParsedData((prev) => {
      if (!prev) return null;
      
      // Set all source columns to "skip" in mappings (they'll be handled by the combination)
      const updatedMappings = prev.mappings.map((m) => {
        if (sourceColumns.includes(m.sourceColumn)) {
          return { ...m, targetField: "skip" as const };
        }
        // Also skip any other column currently mapped to this target field
        if (m.targetField === targetField) {
          return { ...m, targetField: "skip" as const };
        }
        return m;
      });
      
      // Remove any existing combination for this field
      const filteredCombinations = prev.combinations.filter((c) => c.targetField !== targetField);
      
      return {
        ...prev,
        mappings: updatedMappings,
        combinations: [
          ...filteredCombinations,
          { targetField, sourceColumns, separator },
        ],
      };
    });
  }, [parsedData]);

  // Remove a column combination
  const removeCombination = useCallback((targetField: keyof Transaction) => {
    if (!parsedData) return;
    
    setParsedData((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        combinations: prev.combinations.filter((c) => c.targetField !== targetField),
      };
    });
  }, [parsedData]);

  // Validate mappings and get duplicate info
  const validateMappings = useCallback((): { valid: boolean; errors: string[]; duplicateDetails: Map<string, string[]> } => {
    if (!parsedData) return { valid: false, errors: ["No data loaded"], duplicateDetails: new Map() };

    const errors: string[] = [];
    const requiredFields = INVENTORY_FIELDS.filter((f) => f.required);
    
    // Check required fields - can be satisfied by mapping OR combination
    for (const field of requiredFields) {
      const hasMapping = parsedData.mappings.some((m) => m.targetField === field.key);
      const hasCombination = parsedData.combinations.some((c) => c.targetField === field.key);
      if (!hasMapping && !hasCombination) {
        errors.push(`Required field "${field.label}" is not mapped`);
      }
    }

    // Check for duplicate mappings (same target field mapped twice, excluding "skip")
    // Also track which source columns are mapped to the same target
    const fieldToColumns = new Map<string, string[]>();
    for (const mapping of parsedData.mappings) {
      if (mapping.targetField === "skip") continue;
      
      const existing = fieldToColumns.get(mapping.targetField) || [];
      existing.push(mapping.sourceColumn);
      fieldToColumns.set(mapping.targetField, existing);
    }
    
    const duplicateDetails = new Map<string, string[]>();
    for (const [field, columns] of Array.from(fieldToColumns.entries())) {
      if (columns.length > 1) {
        // Check if this field has a combination defined
        const hasCombination = parsedData.combinations.some((c) => c.targetField === field);
        if (!hasCombination) {
          const fieldLabel = INVENTORY_FIELDS.find((f) => f.key === field)?.label || field;
          duplicateDetails.set(fieldLabel, columns);
          errors.push(`"${fieldLabel}" has multiple columns mapped to it: "${columns.join('", "')}". Use "Combine Columns" or change one mapping.`);
        }
      }
    }

    return { valid: errors.length === 0, errors, duplicateDetails };
  }, [parsedData]);

  // Transform rows to transactions based on mappings and combinations
  const transformRowsToTransactions = useCallback((): TransformedRow[] => {
    if (!parsedData) return [];

    return parsedData.rows.map((row, index) => {
      const transaction: Partial<Transaction> = {};

      // Numeric fields that need currency parsing
      const numericFields = ["cost", "sale_price", "marketplace_fees", "shipping_fees"];
      
      // First, apply regular mappings
      for (const mapping of parsedData.mappings) {
        if (mapping.targetField === "skip") continue;

        const value = row[mapping.sourceColumn];
        if (value === undefined || value === "") continue;

        // Handle type conversions
        if (mapping.targetField === "year") {
          const yearNum = parseInt(value, 10);
          if (!isNaN(yearNum)) {
            transaction.year = yearNum;
          }
        } else if (numericFields.includes(mapping.targetField)) {
          // Remove currency symbols and commas, then parse
          const cleanedValue = value.replace(/[$€£,]/g, "").trim();
          const numValue = parseFloat(cleanedValue);
          if (!isNaN(numValue)) {
            (transaction as any)[mapping.targetField] = numValue;
          } else {
            (transaction as any)[mapping.targetField] = value;
          }
        } else {
          (transaction as any)[mapping.targetField] = value.trim();
        }
      }

      // Then, apply column combinations (these override regular mappings)
      for (const combo of parsedData.combinations) {
        const values = combo.sourceColumns
          .map((col) => row[col]?.trim())
          .filter((v) => v && v !== "");
        
        if (values.length > 0) {
          const combinedValue = values.join(combo.separator);
          
          // Handle type conversions for combined values
          if (combo.targetField === "year") {
            const yearNum = parseInt(combinedValue, 10);
            if (!isNaN(yearNum)) {
              transaction.year = yearNum;
            }
          } else if (numericFields.includes(combo.targetField)) {
            const cleanedValue = combinedValue.replace(/[$€£,]/g, "").trim();
            const numValue = parseFloat(cleanedValue);
            if (!isNaN(numValue)) {
              (transaction as any)[combo.targetField] = numValue;
            } else {
              (transaction as any)[combo.targetField] = combinedValue;
            }
          } else {
            (transaction as any)[combo.targetField] = combinedValue;
          }
        }
      }

      return {
        id: index,
        data: transaction,
        originalRow: row,
      };
    });
  }, [parsedData]);

  // Check if a column name looks like a date field
  const isDateColumn = useCallback((columnName: string): boolean => {
    return DATE_FIELD_PATTERNS.some(pattern => pattern.test(columnName));
  }, []);

  // Apply smart extraction to all rows
  const applySmartExtraction = useCallback(() => {
    setTransformedRows((prev) => {
      return prev.map((row) => {
        // For year extraction, only use non-date fields (card name, manufacturer, set, etc.)
        const textForYearExtraction = Object.entries(row.originalRow)
          .filter(([key]) => !isDateColumn(key))
          .map(([, value]) => value)
          .join(" ");
        
        // For other extractions (manufacturer, set, sport, grade), use all text
        const allText = Object.values(row.originalRow).join(" ");
        
        // Only fill in fields that are currently empty
        const updatedData = { ...row.data };
        
        // Extract year ONLY from non-date fields
        if (!updatedData.year) {
          const year = extractYear(textForYearExtraction);
          if (year) updatedData.year = year;
        }
        
        // Extract other fields from all text
        if (!updatedData.manufacturer) {
          const manufacturer = extractManufacturer(allText);
          if (manufacturer) updatedData.manufacturer = manufacturer;
        }
        if (!updatedData.set) {
          const set = extractSet(allText);
          if (set) updatedData.set = set;
        }
        if (!updatedData.sport) {
          const sport = extractSport(allText);
          if (sport) updatedData.sport = sport;
        }
        if (!updatedData.grade) {
          const grade = extractGrade(allText);
          if (grade) updatedData.grade = grade;
        }
        
        return { ...row, data: updatedData };
      });
    });
    setExtractionApplied(true);
  }, [isDateColumn, extractYear, extractManufacturer, extractSet, extractSport, extractGrade]);

  // Update a specific cell in the transformed data
  const updateCell = useCallback((rowId: number, field: keyof Transaction, value: string) => {
    setTransformedRows((prev) => {
      return prev.map((row) => {
        if (row.id !== rowId) return row;
        
        const updatedData = { ...row.data };
        
        // Handle type conversions
        if (field === "year") {
          const yearNum = parseInt(value, 10);
          if (!isNaN(yearNum)) {
            updatedData.year = yearNum;
          } else if (value === "") {
            delete updatedData.year;
          }
        } else if (field === "cost") {
          const cleanedValue = value.replace(/[$€£,]/g, "").trim();
          const numValue = parseFloat(cleanedValue);
          if (!isNaN(numValue)) {
            updatedData.cost = numValue;
          } else if (value === "") {
            delete updatedData.cost;
          } else {
            updatedData.cost = value;
          }
        } else {
          if (value.trim() === "") {
            delete (updatedData as any)[field];
          } else {
            (updatedData as any)[field] = value.trim();
          }
        }
        
        return { ...row, data: updatedData };
      });
    });
    setEditingCell(null);
  }, []);

  // Handle proceeding to preview
  const handleProceedToPreview = useCallback(() => {
    const validation = validateMappings();
    if (!validation.valid) {
      setImportError(validation.errors.join(". "));
      return;
    }
    setImportError(null);
    
    // Transform data for editing
    const transformed = transformRowsToTransactions();
    setTransformedRows(transformed);
    setCurrentPage(0);
    
    setShowMappingDialog(false);
    setShowPreviewDialog(true);
  }, [validateMappings, transformRowsToTransactions]);

  // Handle import
  const handleImport = useCallback(async () => {
    if (!dataService) {
      setImportError("Unable to import: No data service available");
      return;
    }

    setIsImporting(true);
    setImportError(null);

    try {
      const transactions = transformedRows.map((r) => r.data);
      
      if (transactions.length === 0) {
        throw new Error("No valid transactions to import");
      }

      // Filter out transactions missing required fields
      const validTransactions = transactions.filter((t) => 
        t.card && t.purchase_date && (t.cost !== undefined && t.cost !== "")
      );

      if (validTransactions.length === 0) {
        throw new Error("No transactions have all required fields (Card, Date Purchased, Price)");
      }

      if (validTransactions.length < transactions.length) {
        console.warn(`${transactions.length - validTransactions.length} rows skipped due to missing required fields`);
      }

      await dataService.insertTransactions(validTransactions);
      
      setImportSuccess(true);
      setShowPreviewDialog(false);
      setParsedData(null);
      setTransformedRows([]);
      setFileName("");
      onImportComplete();

      // Clear success message after 3 seconds
      setTimeout(() => {
        setImportSuccess(false);
      }, 3000);
    } catch (error: any) {
      console.error("Import error:", error);
      setImportError(error.message || "Failed to import data");
    } finally {
      setIsImporting(false);
    }
  }, [dataService, transformedRows, onImportComplete]);

  // Reset/cancel import
  const handleCancel = useCallback(() => {
    setParsedData(null);
    setTransformedRows([]);
    setShowMappingDialog(false);
    setShowPreviewDialog(false);
    setImportError(null);
    setFileName("");
    setExtractionApplied(false);
    setCurrentPage(0);
  }, []);

  // Pagination
  const totalRows = transformedRows.length;
  const totalPages = Math.ceil(totalRows / ROWS_PER_PAGE);
  const paginatedRows = transformedRows.slice(
    currentPage * ROWS_PER_PAGE,
    (currentPage + 1) * ROWS_PER_PAGE
  );

  // Count mapped fields
  const mappedCount = parsedData?.mappings.filter((m) => m.targetField !== "skip").length || 0;
  const requiredMapped = parsedData?.mappings.filter((m) => 
    INVENTORY_FIELDS.find((f) => f.key === m.targetField && f.required)
  ).length || 0;

  // Get fields that are mapped (for column headers in preview)
  const mappedFieldKeys = useMemo(() => {
    // Return all fields for the editable preview table
    return INVENTORY_FIELDS.map(f => f.key);
  }, []);

  // Count extracted values
  const extractionStats = useMemo(() => {
    if (!extractionApplied) return null;
    
    let yearsExtracted = 0;
    let manufacturersExtracted = 0;
    let setsExtracted = 0;
    let sportsExtracted = 0;
    let gradesExtracted = 0;
    
    transformedRows.forEach((row) => {
      if (row.data.year) yearsExtracted++;
      if (row.data.manufacturer) manufacturersExtracted++;
      if (row.data.set) setsExtracted++;
      if (row.data.sport) sportsExtracted++;
      if (row.data.grade) gradesExtracted++;
    });
    
    return {
      years: yearsExtracted,
      manufacturers: manufacturersExtracted,
      sets: setsExtracted,
      sports: sportsExtracted,
      grades: gradesExtracted,
    };
  }, [extractionApplied, transformedRows]);

  return (
    <Card className="glass-card border-2 border-dashed border-primary/20 hover:border-primary/40 transition-colors">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5 text-primary" />
          Import Inventory
        </CardTitle>
        <CardDescription>
          Upload a CSV or Excel file to bulk import cards into your inventory.
          Required fields: Card Name, Date Purchased, and Purchase Price.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Success Message */}
        {importSuccess && (
          <div className="mb-4 p-4 bg-success/10 border border-success/20 rounded-lg flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-success/20 flex items-center justify-center">
              <Check className="h-5 w-5 text-success" />
            </div>
            <div>
              <p className="font-medium text-success">Import Successful!</p>
              <p className="text-sm text-muted-foreground">
                Your inventory has been updated with the imported cards.
              </p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {importError && !showMappingDialog && !showPreviewDialog && (
          <div className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0" />
            <p className="text-sm text-destructive">{importError}</p>
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto"
              onClick={() => setImportError(null)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Drop Zone */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClickUpload}
          className={`
            relative cursor-pointer rounded-xl border-2 border-dashed transition-all duration-200
            p-8 text-center
            ${isDragging 
              ? "border-primary bg-primary/5 scale-[1.02]" 
              : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50"
            }
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileInputChange}
            className="hidden"
          />
          
          <div className="flex flex-col items-center gap-4">
            <div className={`
              h-16 w-16 rounded-2xl flex items-center justify-center transition-colors
              ${isDragging ? "bg-primary/20" : "bg-muted"}
            `}>
              <Upload className={`h-8 w-8 ${isDragging ? "text-primary" : "text-muted-foreground"}`} />
            </div>
            
            <div>
              <p className="text-lg font-medium">
                {isDragging ? "Drop your file here" : "Drag & drop or click to upload"}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Supports CSV, Excel (.xlsx, .xls)
              </p>
            </div>

            <Button variant="outline" size="sm" className="gap-2">
              <FileSpreadsheet className="h-4 w-4" />
              Browse Files
            </Button>
          </div>
        </div>

        {/* File Info */}
        {fileName && !showMappingDialog && !showPreviewDialog && (
          <div className="mt-4 flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">{fileName}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={handleCancel}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </CardContent>

      {/* Column Mapping Dialog */}
      <Dialog open={showMappingDialog} onOpenChange={(open) => !open && handleCancel()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRight className="h-5 w-5" />
              Map Columns to Inventory Fields
            </DialogTitle>
            <DialogDescription>
              We've detected {parsedData?.headers.length || 0} columns and auto-mapped {mappedCount} of them.
              {requiredMapped < 3 && (
                <span className="text-destructive ml-1">
                  Please ensure all required fields (Card, Date, Price) are mapped.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {/* Error in dialog */}
          {importError && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
              <p className="text-sm text-destructive">{importError}</p>
            </div>
          )}

          {/* Mapping Legend */}
          <div className="flex gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Badge variant="destructive" className="h-5">Required</Badge>
              <span className="text-muted-foreground">Must be mapped</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="h-5">Optional</Badge>
              <span className="text-muted-foreground">Can be skipped</span>
            </div>
          </div>

          {/* Mapping Table */}
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Your Column</TableHead>
                  <TableHead className="w-[60px] text-center"></TableHead>
                  <TableHead className="w-[200px]">Maps To</TableHead>
                  <TableHead>Sample Values</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {parsedData?.mappings.map((mapping) => {
                  const isMapped = mapping.targetField !== "skip";

                  return (
                    <TableRow key={mapping.sourceColumn}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <span className="truncate max-w-[180px]">{mapping.sourceColumn}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <ArrowRight className={`h-4 w-4 ${isMapped ? "text-primary" : "text-muted-foreground"}`} />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={mapping.targetField}
                          onValueChange={(value) => updateMapping(mapping.sourceColumn, value as keyof Transaction | "skip")}
                        >
                          <SelectTrigger className={isMapped ? "border-primary/50" : ""}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="skip">
                              <span className="text-muted-foreground">Skip this column</span>
                            </SelectItem>
                            {INVENTORY_FIELDS.map((field) => (
                              <SelectItem key={field.key} value={field.key}>
                                <span className="flex items-center gap-2">
                                  {field.label}
                                  {field.required && (
                                    <Badge variant="destructive" className="h-4 text-xs px-1">Required</Badge>
                                  )}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {mapping.sampleValues.slice(0, 3).map((val, i) => (
                            <Badge key={i} variant="outline" className="text-xs font-normal truncate max-w-[100px]">
                              {val || "(empty)"}
                            </Badge>
                          ))}
                          {mapping.sampleValues.length === 0 && (
                            <span className="text-xs text-muted-foreground italic">No values</span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Column Combinations Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Link2 className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Combine Columns</span>
                <span className="text-xs text-muted-foreground">(optional)</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setCombineSelectedColumns([]);
                  setCombineTargetField("grade");
                  setCombineSeparator(" ");
                  setShowCombineDialog(true);
                }}
                className="gap-2"
              >
                <Plus className="h-3 w-3" />
                Add Combination
              </Button>
            </div>
            
            <p className="text-xs text-muted-foreground">
              Combine multiple columns into one field. Useful when grading company and grade number are in separate columns (e.g., "PSA" + "10" → "PSA 10").
            </p>

            {/* Existing combinations */}
            {parsedData?.combinations && parsedData.combinations.length > 0 && (
              <div className="space-y-2">
                {parsedData.combinations.map((combo) => {
                  const fieldLabel = INVENTORY_FIELDS.find((f) => f.key === combo.targetField)?.label || combo.targetField;
                  // Get sample combined value from first row
                  const sampleValue = parsedData.rows[0] 
                    ? combo.sourceColumns
                        .map((col) => parsedData.rows[0][col]?.trim())
                        .filter((v) => v)
                        .join(combo.separator)
                    : "";
                  
                  return (
                    <div 
                      key={combo.targetField}
                      className="flex items-center justify-between p-3 bg-primary/5 border border-primary/20 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1">
                          {combo.sourceColumns.map((col, i) => (
                            <React.Fragment key={col}>
                              <Badge variant="outline" className="text-xs">{col}</Badge>
                              {i < combo.sourceColumns.length - 1 && (
                                <span className="text-muted-foreground text-xs">+</span>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                        <ArrowRight className="h-4 w-4 text-primary" />
                        <Badge className="bg-primary/20 text-primary border-0">{fieldLabel}</Badge>
                        {sampleValue && (
                          <span className="text-xs text-muted-foreground">
                            Preview: "{sampleValue}"
                          </span>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCombination(combo.targetField)}
                        className="h-8 w-8 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="gradient" onClick={handleProceedToPreview} className="gap-2">
              <Eye className="h-4 w-4" />
              Preview & Edit Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Combine Columns Dialog */}
      <Dialog open={showCombineDialog} onOpenChange={setShowCombineDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              Combine Columns
            </DialogTitle>
            <DialogDescription>
              Select columns to combine into a single field. Values will be joined in order.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Target Field Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Target Field</label>
              <Select
                value={combineTargetField}
                onValueChange={(value) => setCombineTargetField(value as keyof Transaction)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INVENTORY_FIELDS.map((field) => (
                    <SelectItem key={field.key} value={field.key}>
                      {field.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Column Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Columns to Combine</label>
              <p className="text-xs text-muted-foreground">
                Click columns in the order you want them combined
              </p>
              <div className="flex flex-wrap gap-2 p-3 border rounded-lg min-h-[80px]">
                {parsedData?.headers.map((header) => {
                  const isSelected = combineSelectedColumns.includes(header);
                  const order = combineSelectedColumns.indexOf(header);
                  
                  return (
                    <Badge
                      key={header}
                      variant={isSelected ? "default" : "outline"}
                      className={`cursor-pointer transition-all ${
                        isSelected ? "bg-primary" : "hover:border-primary"
                      }`}
                      onClick={() => {
                        if (isSelected) {
                          setCombineSelectedColumns((prev) => 
                            prev.filter((c) => c !== header)
                          );
                        } else {
                          setCombineSelectedColumns((prev) => [...prev, header]);
                        }
                      }}
                    >
                      {isSelected && (
                        <span className="mr-1 font-bold">{order + 1}.</span>
                      )}
                      {header}
                    </Badge>
                  );
                })}
              </div>
            </div>

            {/* Separator */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Separator</label>
              <div className="flex gap-2">
                {[" ", "-", " - ", "/", ": "].map((sep) => (
                  <Button
                    key={sep}
                    variant={combineSeparator === sep ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCombineSeparator(sep)}
                    className="min-w-[40px]"
                  >
                    {sep === " " ? "space" : `"${sep}"`}
                  </Button>
                ))}
              </div>
            </div>

            {/* Preview */}
            {combineSelectedColumns.length >= 2 && parsedData?.rows[0] && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Preview (first row):</p>
                <p className="font-medium">
                  "{combineSelectedColumns
                    .map((col) => parsedData.rows[0][col]?.trim())
                    .filter((v) => v)
                    .join(combineSeparator)}"
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCombineDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="gradient"
              disabled={combineSelectedColumns.length < 2}
              onClick={() => {
                createColumnCombination(combineTargetField, combineSelectedColumns, combineSeparator);
                setShowCombineDialog(false);
              }}
            >
              Create Combination
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview & Edit Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={(open) => !open && handleCancel()}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="h-5 w-5" />
              Review & Edit Data
            </DialogTitle>
            <DialogDescription>
              Click any cell to edit. Use Smart Extract to auto-fill missing fields from your data.
            </DialogDescription>
          </DialogHeader>

          {/* Error in preview dialog */}
          {importError && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
              <p className="text-sm text-destructive">{importError}</p>
            </div>
          )}

          {/* Smart Extract Button & Stats */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={applySmartExtraction}
                className="gap-2"
                disabled={extractionApplied}
              >
                <Wand2 className="h-4 w-4" />
                {extractionApplied ? "Extraction Applied" : "Smart Extract"}
                <Sparkles className="h-3 w-3 text-yellow-500" />
              </Button>
              
              {extractionApplied && extractionStats && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Found:</span>
                  {extractionStats.years > 0 && (
                    <Badge variant="secondary" className="text-xs">{extractionStats.years} years</Badge>
                  )}
                  {extractionStats.manufacturers > 0 && (
                    <Badge variant="secondary" className="text-xs">{extractionStats.manufacturers} manufacturers</Badge>
                  )}
                  {extractionStats.sets > 0 && (
                    <Badge variant="secondary" className="text-xs">{extractionStats.sets} sets</Badge>
                  )}
                  {extractionStats.sports > 0 && (
                    <Badge variant="secondary" className="text-xs">{extractionStats.sports} sports</Badge>
                  )}
                  {extractionStats.grades > 0 && (
                    <Badge variant="secondary" className="text-xs">{extractionStats.grades} grades</Badge>
                  )}
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <Badge variant="outline">{totalRows} total cards</Badge>
            </div>
          </div>

          {/* Editable Table */}
          <div className="flex-1 border rounded-lg overflow-auto min-h-0">
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead className="w-[50px] sticky left-0 bg-background">#</TableHead>
                  {mappedFieldKeys.map((fieldKey) => {
                    const field = INVENTORY_FIELDS.find((f) => f.key === fieldKey);
                    return (
                      <TableHead key={fieldKey} className="min-w-[120px]">
                        <div className="flex items-center gap-1">
                          {field?.label || fieldKey}
                          {field?.required && (
                            <span className="text-destructive">*</span>
                          )}
                        </div>
                      </TableHead>
                    );
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="text-muted-foreground sticky left-0 bg-background">
                      {row.id + 1}
                    </TableCell>
                    {mappedFieldKeys.map((fieldKey) => {
                      const value = (row.data as any)[fieldKey];
                      const displayValue = value !== undefined ? String(value) : "";
                      const isEditing = editingCell?.rowId === row.id && editingCell?.field === fieldKey;
                      const field = INVENTORY_FIELDS.find((f) => f.key === fieldKey);
                      const isEmpty = !displayValue;
                      const isRequired = field?.required;
                      
                      return (
                        <TableCell 
                          key={fieldKey}
                          className={`
                            cursor-pointer hover:bg-muted/50 transition-colors
                            ${isEmpty && isRequired ? "bg-destructive/5" : ""}
                            ${isEditing ? "p-0" : ""}
                          `}
                          onClick={() => !isEditing && setEditingCell({ rowId: row.id, field: fieldKey })}
                        >
                          {isEditing ? (
                            <Input
                              autoFocus
                              defaultValue={displayValue}
                              className="h-8 border-primary"
                              onBlur={(e) => updateCell(row.id, fieldKey, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  updateCell(row.id, fieldKey, e.currentTarget.value);
                                } else if (e.key === "Escape") {
                                  setEditingCell(null);
                                }
                              }}
                            />
                          ) : (
                            <div className="min-h-[24px] flex items-center">
                              {displayValue || (
                                <span className="text-muted-foreground/50 text-xs italic">
                                  {isRequired ? "Required" : "Click to add"}
                                </span>
                              )}
                            </div>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-sm text-muted-foreground">
                Showing {currentPage * ROWS_PER_PAGE + 1}-{Math.min((currentPage + 1) * ROWS_PER_PAGE, totalRows)} of {totalRows} rows
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm">
                  Page {currentPage + 1} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={currentPage === totalPages - 1}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => {
              setShowPreviewDialog(false);
              setShowMappingDialog(true);
              setTransformedRows([]);
              setExtractionApplied(false);
            }}>
              Back to Mapping
            </Button>
            <Button variant="gradient" onClick={handleImport} disabled={isImporting} className="gap-2">
              {isImporting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  Import {totalRows} Cards
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default InventoryImport;
